// src/components/Pager.jsx
// Page through a table (3.26.0): « First · ‹ Previous · Page 2 of 14 ·
// Next › · Last », under every spreadsheet-like list. usePager(rows, size)
// returns { pageRows, pager } for rows that are already loaded (sorted
// first, so sorting covers every page); <Pager {...pager} /> draws the bar.
// The page goes back to 1 when the number of rows or the page size changes
// (a new search or filter); editing a row keeps the page.
import { useCallback, useEffect, useMemo, useState } from 'react';

export function usePager(rows, size) {
  const per = size === 'all' ? Infinity : Math.max(1, Number(size) || 50);
  const pages = per === Infinity ? 1 : Math.max(1, Math.ceil(rows.length / per));
  const [page, setPage] = useState(1);
  // A new search, filter or page size starts again at page 1
  useEffect(() => { setPage(1); }, [rows.length, per]);
  const current = Math.min(page, pages);
  const pageRows = useMemo(() => (per === Infinity ? rows : rows.slice((current - 1) * per, current * per)), [rows, per, current]);
  return {
    pageRows,
    pager: { page: current, pages, total: rows.length, from: rows.length ? (current - 1) * (per === Infinity ? 0 : per) + 1 : 0, to: (current - 1) * (per === Infinity ? 0 : per) + pageRows.length, onPage: p => setPage(Math.max(1, Math.min(pages, p))) },
  };
}

// Lists the server sends one page at a time (Outbox, Inbox, Usage's sent
// and received lists; 3.26.0). Opening one stays as cheap as before: the
// newest page. Next / Previous ask the server for that page. Sorting by a
// column, or Last, loads every row that matches the filters once; from then
// on sorting and paging cover all of them, here in the browser.
//   const sp = useServerPages(sortedRows, per, req => load(filters, req));
//   in load: fetch with sp.params(req, perPage), then
//   sp.loaded(req, result.hasMore, perPage) (perPage: the search's Show);
//   a new search calls load(filters) (page 1, newest first)
//   render sp.pageRows and <Pager {...sp.pager} />; sorting: sp.sortAll()
export function useServerPages(rows, per, reload) {
  const [st, setSt] = useState({ page: 1, full: false, hasMore: false, goLast: false });
  const local = usePager(rows, per);
  const { onPage: goLocal, pages: localPages } = local.pager;
  useEffect(() => {
    if (st.full && st.goLast) { goLocal(localPages); setSt(x => ({ ...x, goLast: false })); }
  }, [st.full, st.goLast, localPages, goLocal]);
  const n = per === 'all' ? 0 : Math.max(1, Number(per) || 50);
  const perOf = p => (p === 'all' ? 0 : Math.max(1, Number(p) || 50));
  const params = useCallback((req, p) => (req?.full || !perOf(p) ? { limit: 'all', page: 1 } : { limit: String(perOf(p)), page: req?.page || 1 }), []);
  const loaded = useCallback((req, hasMore, p) => setSt({ page: req?.full ? 1 : (req?.page || 1), full: !!req?.full || !perOf(p), hasMore: !!hasMore, goLast: !!req?.goLast }), []);
  const sortAll = useCallback(() => { if (!st.full && (st.hasMore || st.page > 1)) reload({ full: true }); }, [st.full, st.hasMore, st.page, reload]);
  if (st.full) return { ...st, params, loaded, sortAll, pageRows: local.pageRows, pager: local.pager };
  const from = rows.length ? (st.page - 1) * n + 1 : 0;
  return {
    ...st, params, loaded, sortAll, pageRows: rows,
    pager: { page: st.page, pages: null, hasMore: st.hasMore, from, to: rows.length ? from + rows.length - 1 : 0,
      onPage: p => reload({ page: Math.max(1, p) }), onLast: () => reload({ full: true, goLast: true }) },
  };
}

export default function Pager({ page, pages, total, from, to, onPage, onLast, hasMore, label = 'rows' }) {
  // pages == null: the server sends one page at a time and the total isn't
  // known yet; Last loads everything (onLast)
  if (pages == null) {
    if (!to) return null;
    const btn = (text, fn, disabled, aria) => (
      <button type="button" onClick={fn} disabled={disabled} aria-label={aria} style={{ ...S.btn, ...(disabled ? S.btnOff : {}) }}>{text}</button>
    );
    return (
      <nav style={S.bar} aria-label="Pages">
        <span style={S.count}>{from}–{to} {label}{hasMore ? ' (newest first)' : ''}</span>
        {(page > 1 || hasMore) && (
          <span style={S.btns}>
            {btn('« First', () => onPage(1), page === 1, 'First page')}
            {btn('‹ Previous', () => onPage(page - 1), page === 1, 'Previous page')}
            <span style={S.pageText}>Page {page}</span>
            {btn('Next ›', () => onPage(page + 1), !hasMore, 'Next page')}
            {btn('Last »', onLast, !hasMore, 'Last page')}
          </span>
        )}
      </nav>
    );
  }
  if (!total) return null;
  const btn = (text, target, disabled, aria) => (
    <button type="button" onClick={() => onPage(target)} disabled={disabled} aria-label={aria}
      style={{ ...S.btn, ...(disabled ? S.btnOff : {}) }}>{text}</button>
  );
  return (
    <nav style={S.bar} aria-label="Pages">
      <span style={S.count}>{from}–{to} of {total} {label}</span>
      {pages > 1 && (
        <span style={S.btns}>
          {btn('« First', 1, page === 1, 'First page')}
          {btn('‹ Previous', page - 1, page === 1, 'Previous page')}
          <span style={S.pageText}>Page {page} of {pages}</span>
          {btn('Next ›', page + 1, page === pages, 'Next page')}
          {btn('Last »', pages, page === pages, 'Last page')}
        </span>
      )}
    </nav>
  );
}

const S = {
  bar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10, fontSize: 13 },
  count: { color: 'var(--t-78909c)' },
  btns: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  pageText: { color: 'var(--t-37474f)', fontWeight: 600, padding: '0 6px' },
  btn: { padding: '6px 12px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  btnOff: { color: 'var(--c-b0bec5)', border: '1.5px solid var(--c-eceff1)', cursor: 'default' },
};
